import { createFrontendModule } from '@backstage/frontend-plugin-api';
import apiDocsPlugin from '@backstage/plugin-api-docs/alpha';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import catalogGraphPlugin from '@backstage/plugin-catalog-graph/alpha';
import orgPlugin from '@backstage/plugin-org/alpha';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import { isAzureDevOpsAvailable } from '@backstage-community/plugin-azure-devops';
import azureDevOpsPlugin from '@backstage-community/plugin-azure-devops/alpha';
import { isGithubActionsAvailable } from '@backstage-community/plugin-github-actions';
import githubActionsPlugin from '@backstage-community/plugin-github-actions/alpha';
import { isNewRelicDashboardAvailable } from '@backstage-community/plugin-newrelic-dashboard';
import newRelicDashboardPlugin from '@backstage-community/plugin-newrelic-dashboard/alpha';
import { isPluginApplicableToEntity as isPagerDutyAvailable } from '@pagerduty/backstage-plugin';
import pagerDutyPlugin from '@pagerduty/backstage-plugin/alpha';
import { isGithubInsightsAvailable } from '@roadiehq/backstage-plugin-github-insights';
import githubInsightsPlugin from '@roadiehq/backstage-plugin-github-insights/alpha';
import { isSnykAvailable } from 'backstage-plugin-snyk';
import snykPlugin from 'backstage-plugin-snyk/alpha';
import { apiVariants, componentVariants, onVariants } from './variants';

/*
 * Native entity cards and contents, re-scoped to the layouts and conditions
 * the portal has always used. Filters reuse each plugin's legacy predicate
 * (the native defaults differ: e.g. Snyk's ignores `snyk.io/target-name`, the
 * New Relic dashboard and TechDocs have no filter at all). Order and
 * enablement live in `app.extensions` in app-config.yaml.
 */

const catalogEntityModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    catalogPlugin.getExtension('entity-card:catalog/about').override({
      params: {
        filter: onVariants([
          ...componentVariants,
          ...apiVariants,
          'airesource',
          'domain',
        ]),
      },
    }),
    catalogPlugin.getExtension('entity-card:catalog/links').override({
      params: {
        filter: onVariants([
          ...componentVariants,
          ...apiVariants,
          'airesource',
          'group',
          'system',
        ]),
      },
    }),
    catalogPlugin
      .getExtension('entity-card:catalog/has-subcomponents')
      .override({ params: { filter: onVariants(componentVariants) } }),
    catalogPlugin
      .getExtension('entity-card:catalog/has-components')
      .override({ params: { filter: onVariants(['system']) } }),
    catalogPlugin
      .getExtension('entity-card:catalog/has-resources')
      .override({ params: { filter: onVariants(['system']) } }),
    catalogPlugin
      .getExtension('entity-card:catalog/has-systems')
      .override({ params: { filter: onVariants(['domain']) } }),
  ],
});

const catalogGraphEntityModule = createFrontendModule({
  pluginId: 'catalog-graph',
  extensions: [
    catalogGraphPlugin
      .getExtension('entity-card:catalog-graph/relations')
      .override({
        params: {
          filter: onVariants([
            ...componentVariants,
            ...apiVariants,
            'airesource',
            'system',
            'domain',
          ]),
        },
      }),
  ],
});

const apiDocsEntityModule = createFrontendModule({
  pluginId: 'api-docs',
  extensions: [
    apiDocsPlugin
      .getExtension('entity-card:api-docs/has-apis')
      .override({ params: { filter: onVariants(['system']) } }),
    apiDocsPlugin
      .getExtension('entity-card:api-docs/providing-components')
      .override({ params: { filter: onVariants(apiVariants) } }),
    apiDocsPlugin
      .getExtension('entity-card:api-docs/consuming-components')
      .override({ params: { filter: onVariants(apiVariants) } }),
    apiDocsPlugin.getExtension('entity-content:api-docs/apis').override({
      params: {
        path: '/api',
        title: 'API',
        filter: onVariants(['service']),
        // Same tables as native, without a definition button for mcp-server APIs.
        loader: () =>
          import('./ApisContent').then(m => <m.ApisContent />),
      },
    }),
    apiDocsPlugin.getExtension('entity-content:api-docs/definition').override({
      params: { filter: onVariants(['api']) },
    }),
  ],
});

const orgEntityModule = createFrontendModule({
  pluginId: 'org',
  extensions: [
    orgPlugin
      .getExtension('entity-card:org/ownership')
      .override({
        params: { type: 'info', filter: onVariants(['user', 'group']) },
      }),
  ],
});

const techdocsEntityModule = createFrontendModule({
  pluginId: 'techdocs',
  extensions: [
    techdocsPlugin.getExtension('entity-content:techdocs').override({
      params: { filter: onVariants(componentVariants) },
    }),
  ],
});

const snykEntityModule = createFrontendModule({
  pluginId: 'snyk',
  extensions: [
    snykPlugin.getExtension('entity-card:snyk').override({
      params: {
        type: 'info',
        filter: onVariants([...componentVariants, 'group'], isSnykAvailable),
      },
    }),
    snykPlugin.getExtension('entity-content:snyk').override({
      params: { filter: onVariants(componentVariants, isSnykAvailable) },
    }),
  ],
});

const newRelicDashboardEntityModule = createFrontendModule({
  pluginId: 'newrelic-dashboard',
  extensions: [
    newRelicDashboardPlugin
      .getExtension('entity-card:newrelic-dashboard/EntityNewRelicDashboardCard')
      .override({
        params: {
          type: 'info',
          filter: onVariants(componentVariants, isNewRelicDashboardAvailable),
        },
      }),
    newRelicDashboardPlugin
      .getExtension(
        'entity-content:newrelic-dashboard/EntityNewRelicDashboardContent',
      )
      .override({
        params: {
          path: '/new-relic',
          title: 'New Relic',
          filter: onVariants(componentVariants, isNewRelicDashboardAvailable),
        },
      }),
  ],
});

const pagerDutyEntityModule = createFrontendModule({
  pluginId: 'pagerduty',
  extensions: [
    pagerDutyPlugin
      .getExtension('entity-card:pagerduty/EntityPagerDutySmallCard')
      .override({
        params: {
          type: 'info',
          filter: onVariants(componentVariants, isPagerDutyAvailable),
        },
      }),
  ],
});

const githubInsightsFilter = onVariants(
  componentVariants,
  isGithubInsightsAvailable,
);

const githubInsightsEntityModule = createFrontendModule({
  pluginId: 'code-insights',
  extensions: [
    githubInsightsPlugin
      .getExtension('entity-card:code-insights/languages')
      .override({ params: { type: 'info', filter: githubInsightsFilter } }),
    githubInsightsPlugin
      .getExtension('entity-card:code-insights/readme')
      .override({ params: { filter: githubInsightsFilter } }),
    githubInsightsPlugin
      .getExtension('entity-card:code-insights/releases')
      .override({ params: { type: 'info', filter: githubInsightsFilter } }),
    githubInsightsPlugin
      .getExtension('entity-card:code-insights/contributors')
      .override({ params: { type: 'info', filter: githubInsightsFilter } }),
    githubInsightsPlugin.getExtension('entity-content:code-insights').override({
      params: {
        path: '/code-insights',
        title: 'Code Insights',
        filter: onVariants(['service'], isGithubInsightsAvailable),
      },
    }),
  ],
});

const githubActionsEntityModule = createFrontendModule({
  pluginId: 'github-actions',
  extensions: [
    githubActionsPlugin.getExtension('entity-content:github-actions').override({
      params: {
        path: '/github-actions',
        title: 'GitHub Actions',
        filter: onVariants(componentVariants, isGithubActionsAvailable),
      },
    }),
  ],
});

const azureDevOpsEntityModule = createFrontendModule({
  pluginId: 'azure-devops',
  extensions: [
    azureDevOpsPlugin
      .getExtension('entity-content:azure-devops/git-tags')
      .override({
        params: {
          path: '/git-tags',
          title: 'Git Tags',
          filter: onVariants(['website'], isAzureDevOpsAvailable),
        },
      }),
  ],
});

export const nativeEntityOverrideModules = [
  catalogEntityModule,
  catalogGraphEntityModule,
  apiDocsEntityModule,
  orgEntityModule,
  techdocsEntityModule,
  snykEntityModule,
  newRelicDashboardEntityModule,
  pagerDutyEntityModule,
  githubInsightsEntityModule,
  githubActionsEntityModule,
  azureDevOpsEntityModule,
];
